import { Fragment } from 'react';
import { FiMapPin } from 'react-icons/fi';
import { formatDisplayShort } from '../../../lib/dateUtils';
import Button from '../../../components/ui/Button';
import SiteStatusBadge from './SiteStatusBadge';
import SiteSessionAction from './SiteSessionAction';

/**
 * Controlled desktop sites table. All state, mutations, hover-debounce refs, and
 * data fetching stay in the parent (Sites.jsx); this component only renders rows
 * and forwards events via props.
 */
export default function SiteTableDesktop({
  filteredSites,
  inProgressBySite,
  defectCountBySiteId,
  dueThisMonthIds,
  showUpcomingDivider,
  hasSites,
  onRowMouseEnter,
  onRowMouseLeave,
  onRowClick,
  onEdit,
}) {
  return (
    <div className="sites-desktop-main">
      <div className="table-container sites-table-desktop">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Address</th>
              <th>Status</th>
              <th>Next Test Due Date</th>
              <th>Test</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let upcomingDividerRendered = false;
              return filteredSites?.map((site) => {
                const addressDisplay =
                  [site.address, site.city, site.state, site.postcode].filter(Boolean).join(', ') || '—';
                const inProgressSessionsForSite = inProgressBySite.get(Number(site.id)) ?? [];
                const meta = defectCountBySiteId.get(String(site.id));
                const isDueThisMonth = dueThisMonthIds.has(site.id);
                const renderDivider = showUpcomingDivider && !isDueThisMonth && !upcomingDividerRendered;
                if (renderDivider) upcomingDividerRendered = true;
                return (
                  <Fragment key={site.id}>
                    {renderDivider && (
                      <tr className="site-filter-divider-row" aria-hidden="true">
                        <td colSpan={5}>Upcoming</td>
                      </tr>
                    )}
                    <tr
                      className="site-table-row-clickable"
                      onMouseEnter={() => onRowMouseEnter(site.id)}
                      onMouseLeave={() => onRowMouseLeave()}
                      onClick={() => onRowClick(site.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(site.id);
                        }
                      }}
                    >
                      <td>
                        {site.display_name ?? site.name?.trim() ?? site.site_code ?? 'Unnamed site'}
                      </td>
                      <td>
                        <span className="site-address-text">
                          <FiMapPin aria-hidden />
                          {addressDisplay}
                        </span>
                      </td>
                      <td>
                        <SiteStatusBadge
                          count={meta?.count ?? 0}
                          isLoading={meta?.isLoading ?? true}
                          isError={meta?.isError ?? false}
                        />
                      </td>
                      <td>
                        {site.next_test_due_date ? (
                          formatDisplayShort(site.next_test_due_date)
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {onEdit || inProgressSessionsForSite.length > 0 ? (
                          <div className="sites-table-resume-stack">
                            {onEdit && (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={(e) => { e.stopPropagation(); onEdit(site); }}
                              >
                                Edit
                              </Button>
                            )}
                            {inProgressSessionsForSite.map((s) => (
                              <SiteSessionAction key={s.id} session={s} siteId={site.id} size="sm" />
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              });
            })()}
          </tbody>
        </table>
        {!hasSites && (
          <p className="empty-state">No sites yet. Add your first site to get started.</p>
        )}
      </div>
    </div>
  );
}
